import { useState, useEffect } from 'react';
import styles from './EditScenarioWorld.module.css';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import { useDevice } from '../../context/DeviceContext';
import { ScenarioCharacterCard } from './ScenarioCharacterCard';
import EditScenarioCharactersSearch from './EditScenarioCharactersSearch';
import { CharacterListResponse } from '../../api/types';
import { FaInfoCircle } from 'react-icons/fa';

interface EditScenarioWorldProps {
  definition: ScenarioDefinitionType;
  onUpdate: (updatedDefinition: ScenarioDefinitionType) => void;
}

function EditScenarioWorld({ definition, onUpdate }: EditScenarioWorldProps): JSX.Element {
  const { isMobile } = useDevice();
  const [showSearch, setShowSearch] = useState(!isMobile); // Always show on desktop
  const [showWorldInfoPill, setShowWorldInfoPill] = useState(false);

  // Handle device type changes
  useEffect(() => {
    if (!isMobile) {
      setShowSearch(true);
    }
  }, [isMobile]);

  const handleAddCharacter = () => {
    setShowSearch(true);
  };

  const handleCloseSearch = () => {
    if (isMobile) {
      setShowSearch(false);
    }
  };

  const handleSelectCharacter = async (character: CharacterListResponse) => {
    console.log('Selected character:', character);
    try {
      const updatedDefinition = await definition.addAvailableCharacter(character.id);
      onUpdate(updatedDefinition);
    } catch (error) {
      console.error('Failed to add available character:', error);
    }
  };

  const handleDeleteCharacter = async (characterId: string) => {
    console.log('Delete character with ID:', characterId);
    
    // On desktop, show a confirmation dialog
    if (!isMobile) {
      const confirmDelete = window.confirm('Are you sure you want to remove this character?');
      if (!confirmDelete) {
        return;
      }
    }
    
    try {
      const updatedDefinition = await definition.removeAvailableCharacter(characterId);
      onUpdate(updatedDefinition);
    } catch (error) {
      console.error('Failed to delete available character:', error);
    }
  };

  const toggleWorldInfoPill = () => {
    setShowWorldInfoPill(!showWorldInfoPill);
  };

  // Get all existing character IDs from the scenario
  const getExistingCharacterIds = (): string[] => {
    const scenarioCharacterIds = definition.characters?.map(char => char.id) || [];
    const worldCharacterIds = definition.available_characters?.map(char => char.id) || [];
    return [...scenarioCharacterIds, ...worldCharacterIds];
  };

  return (
    <div className={styles.editCharactersContainer}>
      <div className={`${styles.sectionHeader} ${isMobile ? styles.mobileHeader : ''}`}>
        <h3 className={styles.sectionTitle}>WORLD</h3>
        <div className={styles.headerButtons}>
          {isMobile && (
            <button 
              onClick={handleAddCharacter}
              className={styles.addButton}
              aria-label="Add new character"
            >
              +
            </button>
          )}
        </div>
      </div>
      
      <div className={styles.sectionHeader}>
        <div className={styles.titleWithInfo}>
          <h3 className={styles.sectionTitle}>CHARACTERS</h3>
          <div className={styles.worldInfoContainer}>
            <button 
              onClick={toggleWorldInfoPill}
              className={`${styles.infoButton} ${styles.worldInfoButton}`}
              aria-label="Show information about world characters"
            >
              <FaInfoCircle className={styles.infoIcon} />
            </button>
            {showWorldInfoPill && (
              <div className={styles.infoPillPopup}>
                <span className={styles.infoPillText}>
                  World characters are not in the starting scene but will appear later in the scenario.
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={styles.headerButtons}>
        </div>
      </div>
      
      <div className={styles.charactersGrid}>
        {definition.available_characters && definition.available_characters.length > 0 ? (
          definition.available_characters.map((character) => (
            <ScenarioCharacterCard 
              key={character.id} 
              character={character}
              showDeleteButton={true}
              onDeleteClick={() => handleDeleteCharacter(character.id)}
              isSearchResult={false}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <p>No characters have been added to this world yet.</p>
          </div>
        )}
      </div>
      
      <EditScenarioCharactersSearch
        isVisible={showSearch}
        onClose={handleCloseSearch}
        onSelectCharacter={handleSelectCharacter}
        existingCharacterIds={getExistingCharacterIds()}
      />
    </div>
  );
}

export default EditScenarioWorld; 